import type Phaser from 'phaser';
import { PARTY_MODES } from '../../meta/online/duel';
import type { RoomMode } from '../../meta/online/live';
import { label, onTap, UI } from './text';
import { drawBossBadge } from './zoneArt';

/**
 * The live modes as tiles (owner, 2026-10-29: CLUB > VERSUS shows them all, instead of one PLAY A
 * FRIEND button hiding them): an icon, the name, and how many can play. Tapping one picks it.
 */
export interface ModeTile {
  mode: RoomMode;
  name: string;
  /** One line of what it is, for the mode's setup. */
  rules: string;
}

export const MODE_TILES: readonly ModeTile[] = [
  { mode: 'race', name: 'RACE', rules: 'Twin lawns, the same mower. First to finish wins.' },
  {
    mode: 'duel',
    name: 'HEAD TO HEAD',
    rules: 'One lawn for everyone, the same mower. Most grass wins.',
  },
  {
    mode: 'soccer',
    name: 'SOCCER',
    rules: 'Kick the ball into the far goal. First to the target, or most at the whistle.',
  },
  {
    mode: 'roundup',
    name: 'ROUND-UP',
    rules: 'Nudge sheep into your own pen. First to the target, or most at the whistle.',
  },
  { mode: 'maze', name: 'MAZE', rules: 'A hedge maze from the corners. First to the star wins.' },
];

/** How many can play a mode, in a few words. */
export function modePlayers(mode: RoomMode): string {
  if (!PARTY_MODES.includes(mode)) return '2 players';
  return mode === 'soccer' ? '2 or 4' : '2 to 4';
}

const TILE_H = 46;
/** Short screens (an iPad's 4:3): a smaller icon, no player count. */
const COMPACT_H = 27;
const GAP = 4;

/**
 * Draws the mode tiles from `y` (two across, then three), each calling `onPick` when tapped;
 * `compact` for short screens. Returns the y just below them.
 */
export function drawModeTiles(
  scene: Phaser.Scene,
  y: number,
  width: number,
  onPick: (mode: RoomMode) => void,
  compact = false,
): number {
  const tileH = compact ? COMPACT_H : TILE_H;
  const g = scene.add.graphics();
  const rows: ModeTile[][] = [MODE_TILES.slice(0, 2), MODE_TILES.slice(2)];
  for (const row of rows) {
    const w = Math.floor((width - 8 - GAP * (row.length - 1)) / row.length);
    row.forEach((tile, i) => {
      const x = 4 + i * (w + GAP);
      g.fillStyle(UI.shadow).fillRect(x + 1, y + 2, w, tileH);
      g.fillStyle(UI.panelHi).fillRect(x, y, w, tileH);
      g.fillStyle(0xffffff, 0.3).fillRect(x + 1, y + 1, w - 2, 1);
      g.lineStyle(1, UI.shadow).strokeRect(x + 0.5, y + 0.5, w - 1, tileH - 1);
      // The icon, twice size (actual size on a short screen).
      const icon = scene.add.graphics().setScale(compact ? 1 : 2);
      icon.setPosition(Math.round(x + w / 2 - (compact ? 4 : 9)), y + (compact ? 3 : 4));
      drawModeIcon(icon, tile.mode, 0, 0);
      label(scene, x + w / 2, y + (compact ? 15 : 25), tile.name, 1, UI.text).setOrigin(0.5, 0);
      if (!compact) {
        label(scene, x + w / 2, y + 35, modePlayers(tile.mode), 1, UI.dim).setOrigin(0.5, 0);
      }
      const zone = scene.add
        .zone(x, y, w, tileH)
        .setOrigin(0)
        .setInteractive({ useHandCursor: true })
        .setData('label', tile.name);
      onTap(zone, () => onPick(tile.mode));
    });
    y += tileH + GAP + 2;
  }
  return y;
}

/** A mode's 9 x 9 icon: a chequered flag, a lawn split two ways, or the boss badges. */
export function drawModeIcon(
  g: Phaser.GameObjects.Graphics,
  mode: RoomMode,
  x: number,
  y: number,
): void {
  switch (mode) {
    case 'race':
      // A chequered flag on a pole.
      g.fillStyle(0x10180c)
        .fillRect(x, y, 1, 9)
        .fillRect(x + 1, y, 8, 6);
      for (let r = 0; r < 2; r++) {
        for (let c = 0; c < 4; c++) {
          g.fillStyle((r + c) % 2 === 0 ? 0xffffff : 0x3a3d42).fillRect(
            x + 1 + c * 2,
            y + 1 + r * 2,
            2,
            2,
          );
        }
      }
      return;
    case 'duel':
      // One lawn, two mowers coming at each other: yours in yellow, the other's in red.
      g.fillStyle(0x10180c).fillRect(x, y, 9, 9);
      g.fillStyle(0x4e9a3a).fillRect(x + 1, y + 1, 7, 7);
      g.fillStyle(UI.accent)
        .fillRect(x + 1, y + 3, 5, 1)
        .fillRect(x + 5, y + 2, 1, 3)
        .fillRect(x + 6, y + 3, 1, 1);
      g.fillStyle(0xe8413c)
        .fillRect(x + 3, y + 6, 5, 1)
        .fillRect(x + 3, y + 5, 1, 3)
        .fillRect(x + 2, y + 6, 1, 1);
      return;
    case 'soccer':
      drawBossBadge(g, 'show', x, y);
      return;
    case 'roundup':
      drawBossBadge(g, 'sheep', x, y);
      return;
    case 'maze':
      drawBossBadge(g, 'maze', x, y);
      return;
  }
}
